import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LayersOutlined as DrawOverIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  PhoneAndroid as PhoneIcon,
} from '@mui/icons-material';
import { useTranslation } from '../i18n/translations';

interface DrawOverAppsPromptProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const STORAGE_KEY = 'ziawellnav_draw_over_apps_prompted';

/**
 * Check if user has already been prompted for draw-over-apps
 */
export function hasBeenPrompted(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

/**
 * Mark that user has been prompted
 */
export function markAsPrompted(): void {
  localStorage.setItem(STORAGE_KEY, 'true');
}

/**
 * Reset the prompt (for testing)
 */
export function resetPrompt(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Hook to manage draw-over-apps prompt state
 */
export function useDrawOverAppsPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Only show on Android Telegram
    const isAndroid = /android/i.test(navigator.userAgent);
    const isTelegramWebApp = !!(window as any).Telegram?.WebApp;
    
    if (isAndroid && isTelegramWebApp && !hasBeenPrompted()) {
      // Small delay so it doesn't interfere with initial load
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismissPrompt = () => {
    markAsPrompted();
    setShowPrompt(false);
  };

  return {
    showPrompt,
    dismissPrompt,
  };
}

/**
 * Component that explains draw-over-apps permission and guides user to enable it
 */
export const DrawOverAppsPrompt: React.FC<DrawOverAppsPromptProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      label: t('drawOverWhyEnable'),
      description: t('drawOverWhyEnableDesc'),
      icon: <DrawOverIcon />,
    },
    {
      label: t('drawOverOpenSettings'),
      description: t('drawOverOpenSettingsDesc'),
      icon: <SettingsIcon />,
    },
    {
      label: t('drawOverEnableForTelegram'),
      description: t('drawOverEnableForTelegramDesc'),
      icon: <PhoneIcon />,
    },
  ];

  const handleOpenSettings = () => {
    // Android intent to open app overlay settings
    // This works in Telegram WebApp on Android
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.openLink) {
        // Try to open the specific setting
        tg.openLink('https://support.google.com/android/answer/9431959', {
          try_instant_view: false
        });
      } else {
        // Fallback: show instructions
        alert(
          'To enable Draw Over Apps:\n\n' +
          '1. Open your phone Settings\n' +
          '2. Go to Apps → Telegram\n' +
          '3. Tap "Display over other apps"\n' +
          '4. Enable the permission'
        );
      }
    } catch (e) {
      console.warn('Could not open settings:', e);
    }
    setActiveStep(2);
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      markAsPrompted();
      onComplete();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleSkip = () => {
    markAsPrompted();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <DrawOverIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {t('drawOverEnhanceNav')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('drawOverDesc')}
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: activeStep >= index 
                        ? 'primary.main' 
                        : alpha(theme.palette.text.primary, 0.1),
                      color: activeStep >= index 
                        ? 'primary.contrastText' 
                        : 'text.secondary',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {activeStep > index ? (
                      <CheckIcon sx={{ fontSize: 18 }} />
                    ) : (
                      step.icon
                    )}
                  </Box>
                )}
              >
                <Typography fontWeight={500}>{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>
                {index === 1 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={handleOpenSettings}
                    sx={{ mb: 1 }}
                  >
                    {t('drawOverOpenSettings')}
                  </Button>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSkip} color="inherit" size="small">
          {t('drawOverSkip')}
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          color="primary"
        >
          {activeStep === steps.length - 1 ? t('drawOverDone') : t('drawOverNext')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrawOverAppsPrompt;
